import { FC } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { colors, fontWeights } from '../../theme/tokens';
import { WizardStep } from './types';

// The stepper of XD screens 3, 4 and 5: three numbered circles
// ("01", "02", "03") with "Étape N" and the step name on the right. The active
// step is an orange outline (transparent fill); the ones already done are ticked; the rest in white.
//
// Mobile (ROADMAP.md, Part 2): circles without a label except the active one's.

export const STEPS: Array<{ step: WizardStep; label: string }> = [
  { step: 1, label: 'Charger le fichier' },
  { step: 2, label: 'Saisir les données' },
  { step: 3, label: 'Valider les infos' },
];

type Props = {
  active: WizardStep;
};

const CIRCLE = 40;

const WizardStepper: FC<Props> = ({ active }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  return (
    <Box
      component="ol"
      aria-label="Étapes"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 1.5 : 3,
        flexWrap: 'wrap',
      }}
    >
      {STEPS.map(({ step, label }) => {
        const isActive = step === active;
        const isDone = step < active;
        const showLabel = !isMobile || isActive;
        const color = isActive ? colors.accent : colors.white;

        return (
          <Box
            component="li"
            key={step}
            aria-current={isActive ? 'step' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: showLabel ? '1 1 auto' : '0 0 auto',
              minWidth: 0,
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: CIRCLE,
                height: CIRCLE,
                flex: '0 0 auto',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${color}`,
                backgroundColor: 'transparent',
                // XD: the current step is outlined, not filled — orange
                // border and number on the transparent circle.
                color,
                fontWeight: fontWeights.semiBold,
                fontSize: '1.125rem',
              }}
            >
              {isDone ? <CheckIcon fontSize="small" /> : `0${step}`}
            </Box>
            {showLabel && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    color,
                    opacity: isActive ? 1 : 0.8,
                  }}
                >
                  Étape {step}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: '0.9375rem',
                    lineHeight: 1.3,
                    fontWeight: fontWeights.semiBold,
                    color,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default WizardStepper;
